import React, { ReactElement, useEffect, useState, useRef } from 'react';
import { getLabelFallback, luhnCheck, onKeyPressOnlyNumber, onKeyPressAlpha, getActualYear, getActualMonth, handleHTTPError} from '@utils/utils';
import { useForm, FormProvider } from 'react-hook-form';
import { CARD_VALIDATION } from '@utils/const';
import cloneDeep from 'clone-deep';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '@redux-actions/';
import Cookies from 'js-cookie';
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate, useLocation} from 'react-router';

import InputElement from '@components/InputElement';
import CVNPopup from '@components/CVNPopup';
import CalendarInput from '@components/CalendarInput';

import { addPayment, preparePayment, submitPayment } from '@utils/services';

const CreditCardForm = () : ReactElement => {
    const methods = useForm<any>({
      mode: 'onChange',
      reValidateMode: 'onChange'
    });
    const navigate = useNavigate();
    const { search } = useLocation();

    const address = useSelector(state => state.cart.address);
    const config = useSelector(state => state.cart.config);
    const cart = useSelector(state => state.cart.cart);

    const [showCVNPopup, setShowCVNPopup] = useState(false);

    const ref = useRef(null);
    const dispatch = useDispatch();

    const sitekey = config.reCaptcha && config.reCaptcha.siteKey ? config.reCaptcha.siteKey : '';
    const reRef = React.useRef<ReCAPTCHA>();

    const getRecaptchaToken = async (): Promise<string> => {
        const token = await reRef.current.executeAsync();
        reRef.current.reset();
    
        return token;
    }

    const formValues = methods.watch(['credit-card-security']);
    
    const [circuit, setCircuit] = useState<string>('generic');
    
    const recognizeCardCircuit = (value: string): string => {
        let circuitTemp = 'generic';
    
        if (!value) {
          return circuitTemp;
        }
    
        for (let cardValidation of Object.values(CARD_VALIDATION)) {
          if (cardValidation.type !== 'generic' && cardValidation.pattern.test(value)) {
            circuitTemp = cardValidation.type;
          }
        }
    
        return circuitTemp;
    }

    const onChangeCNum = (v: string) => {
        //remove "-" for validation
        const value = v.replace(/[- ]/g, '');
        const recognizedCircuit = recognizeCardCircuit(value)
        setCircuit(recognizedCircuit);
        methods.setValue('credit-card-number', v.replace(/-/g, '').match(/.{1,4}/g)?.join('-').substring(0,19))
        return recognizedCircuit;
    }

    const isValidCNum = (v: string): string => {
        //remove "-" for validation
        const value = v.replace(/[- ]/g, '').slice(0, 16);
    
        if (!value) {
          return getLabelFallback('CREDIT_CARD_NUMBER_INVALID');
        }
    
        const recognizedCircuit = onChangeCNum(value);
    
        if (!luhnCheck(value)) {
          return getLabelFallback('CREDIT_CARD_NUMBER_INVALID');
        }
    
        if (formValues["credit-card-security"]) {
          methods.trigger("credit-card-security");
        }
    
        //check format and length
        const format = cloneDeep(CARD_VALIDATION[recognizedCircuit]?.format);
    
        if (
          !(format?.test(value))
          || !(CARD_VALIDATION[recognizedCircuit].length.includes(value.toString().length))
        ) {
          return getLabelFallback('CREDIT_CARD_NUMBER_INVALID');
        }
    }
   
    const validateDate = (dateStr) => {
      const values = dateStr.split('/');
      const month = parseInt(values[0]);
      const year = parseInt(values[1]);
      const actualYear = parseInt(getActualYear().toString().substring(2, 4));
      const actualMonth = parseInt(getActualMonth());
      if(month > 12  ||
        year < actualYear ||
        (year === actualYear && month < actualMonth)
        || isNaN(year)
        ) {
          return false;
        } 
    
       return true;
    }     
   
    const onFormSubmit = async (data) => {
      const reCaptchaToken = config.reCaptcha && config.reCaptcha.siteKey ? await getRecaptchaToken() : '';
      let expMonth = (new Date(data['exp-date-credit-card']).getMonth() + 1).toString();
      if(expMonth.length === 1) expMonth = '0' + expMonth;
      const expYear = (new Date(data['exp-date-credit-card']).getFullYear().toString());

      const customerWebId = config.onBehalfSession === true ? data['webId'] : '';
      const orderId = config.onBehalfSession === true && !!cart && !!cart.orderId ? {'orderId': cart.orderId} : {};

      const addPaymentData = {
          "storeId" : config.storeId,
          "valueFromProfileOrder": "",
          "valueFromPaymentTC": "",
          "paymentTCId": "",
          "payMethodId": CARD_VALIDATION[circuit].payMethodId,
          "piAmount": cart.grandTotal,
          "calculationUsage": "-1,-2,-3,-4,-5,-6,-7",
          "customerWebId": customerWebId,
          "billing_address_id": !!address ? !!address.billingAddressId ? address.billingAddressId : address.deliveryAddressId : null,
          "cc_brand": circuit,
          "cc_cvc": data['credit-card-security'],
          "account": data['credit-card-number'].replace(/-/g, ''),
          "expire_month": expMonth,
          "expire_year": expYear,
          "check_routing_number": "",
          "checkingAccountNumber": "",
          "checkRoutingNumber": "",
          "forterToken": Cookies.get('forterToken'),
          "reCaptchaToken": reCaptchaToken, 
          "checkWarranty": "true",
          ...orderId
      }

	    dispatch(actions.startLoading());
        addPayment(config.hostname, addPaymentData)
          .then((res: any) => {
            if(res.status === 200) {
              preparePayment(config.hostname, orderId)
                .then((res: any) => {
                  if(res.status === 200) {

                    const submitPaymnetData = {
                      "orderId": cart.orderId,
                      "notifyOrderSubmitted": "1",
                      "notifyMerchant": "1",
                      "notifyShopper": "1",
                      "notify_EMailSender_recipient": "",
                      "purchaseorder_id": ""
                    }

                    submitPayment(config.hostname, submitPaymnetData)
                      .then((res: any) => {
                        dispatch(actions.startLoading());
                        dispatch(actions.setOrderId(res.data.orderId));
                        if(typeof $TB != 'undefined' && !!$TB.hooks) {
                          $TB.hooks.fireEvent("pageChange", null, {"sendBeacon": true});
                        }
                        navigate(config.pagesUrl.typ + search);
                      })
                      .catch(err => {
                        console.error('>>> Submit payment => error status:', err.status, ', error: ', err);
                        handleHTTPError(dispatch, actions, err, 'payment');
                        dispatch(actions.errorLoading())
                      })
                    }
                })
                .catch(err => {
                  console.error('>>> Prepare payment => error status:', err.status, ', error: ', err);
                  handleHTTPError(dispatch, actions, err);
                  dispatch(actions.errorLoading())
                })
            }
          })
          .catch(err => {
            console.error('>>> Add payment => error status:', err.status, ', error: ', err);
            handleHTTPError(dispatch, actions, err);
            dispatch(actions.errorLoading())
          })
    };

    return(
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onFormSubmit)}>
          <div className="credit-card-form">
              <InputElement id={"credit-card-number"} 
                  type={'tel'}
                  placeholder={getLabelFallback('CREDIT_CARD_PAYMENT_CARD_NUMBER_LABEL')}
                  required
                  onChange={(value) => {onChangeCNum(value)}}
                  inputMode={"numeric"}
                  validate= {(value) => isValidCNum(value)}
                  maxLength={19}
                  onKeyPressInput={e => onKeyPressOnlyNumber(e)}
              />
              <InputElement id={"name-credit-card"}
                  type={'text'}
                  placeholder={getLabelFallback('CREDIT_CARD_PAYMENT_NAME_LABEL')}
                  required 
                  claim={getLabelFallback('CREDIT_CARD_PAYMENT_CLAIM_LABEL')}
                  maxLength={64}
                  onKeyPressInput={(value) => {onKeyPressAlpha(value)}}
              />
              <div className="credit-card-form-row">
                <CalendarInput 
                  disableFuture={false}
                  minDate={new Date()}
                  placeholder={getLabelFallback('CREDIT_CARD_EXPIRATION_DATE_LABEL')}
                  id={'exp-date-credit-card'}
                  required
                  onChange={(value) => methods.setValue('exp-date-credit-card', value)}
                  defaultValue={undefined}
                  validate={(value) => validateDate(value)}
                  mask="__/__"
                  inputFormat='MM/yy'
                  views={['month', 'year']}
                  maxLength={5}
                  onChangeInput={(e) => {
                    if(e.keyCode !== 8 && e.keyCode !== 46) {
                      const value = document.getElementById('exp-date-credit-card').value;
                      const values = value.split('/');
                      if(values[0].length === 2) {
                        const year = !!values[1] ? values[1] : '';
                        document.getElementById('exp-date-credit-card').value = values[0] + '/' + year;
                      }
                    }                
                  }}
                />
              </div>
              <InputElement id={"credit-card-security"} 
                  type={'tel'}
                  placeholder={getLabelFallback('CREDIT_CARD_SECURITY_LABEL')}
                  required 
                  pattern={{
                      value: CARD_VALIDATION[circuit]?.cvcPattern,
                      message: getLabelFallback('CREDIT_CARD_CVV_REQUIRED'),
                    }}
                  classes={"w-75"}
                  maxLength={4}
                  onKeyPressInput={e => onKeyPressOnlyNumber(e)}
              />
              <div className="primary-button-container">
                <button className="primary-button" onClick={(e) => { e.preventDefault(); setShowCVNPopup(showCVNPopup => showCVNPopup = !showCVNPopup)}} >{ getLabelFallback('CREDIT_CARD_FIND_SECURITY_LABEL') }</button>
                {showCVNPopup && 
                    <CVNPopup onClick={() => setShowCVNPopup(false)}/>
                }
              </div>

              {config.onBehalfSession === true &&
                <InputElement id={"webId"}
                    type={'text'}
                    placeholder={getLabelFallback('WEB_ID_LABEL')}
                    required 
                    maxLength={64}
                    onKeyPressInput={(value) => {onKeyPressOnlyNumber(value)}}
                />
              }
              
              <button className="violet-button" type="submit">{getLabelFallback('PAY_CONFIRM_BUTTON_LABEL')}</button>
          </div>
          {config.reCaptcha.enabled && 
            <ReCAPTCHA
              sitekey={sitekey}
              ref={reRef}
              theme={config.reCaptcha.theme}
              size={config.reCaptcha.size}
            />
          }
          
        </form>
      </FormProvider>
    )
}

export default CreditCardForm;